import React from 'react';

import { Icon, IconTypes } from '@eika/icon';
import { RichIconTypes } from '@eika/rich-icon';

import useTranslation from '../../../hooks/useTranslation';
import TranslationsConfigType, {
    ErrorCodesBackend,
    ErrorDescription,
    ErrorsConfigType,
} from '../../../translations/translationType';
import ErrorCard from '../../errorcard/ErrorCard';

type RequestExceptionWithResponse = import('@eika-infrastruktur/request').RequestExceptionWithResponse;

enum ErrorCodes {
    TIMEOUT = 'DAG2820005',
    INTERNAL_ERROR = 'DAG2820001',
}

export interface ErrorProps extends React.PropsWithChildren {
    className?: string;
    title?: string;
    exception?: RequestExceptionWithResponse | unknown;
    render?: (code: ErrorCodes) => React.JSX.Element | string;
    defaultError?: string;
    iconType?: React.JSX.Element | IconTypes | RichIconTypes | undefined;
    isModal?: boolean;
    hideErrorCode?: boolean;
}

const defaultRender = (errors: ErrorsConfigType, defaultError?: string) => (
    <p>{defaultError ?? errors.defaultMessage}</p>
);

const selectIcon = (isTimeout: boolean) => {
    return isTimeout ? (
        <Icon type={IconTypes.BANDAGED_HEART} />
    ) : (
        <Icon className="error" type={IconTypes.UTROPSTEGN_36} />
    );
};

const findKnownError = (
    exception: RequestExceptionWithResponse | undefined,
    translated: TranslationsConfigType,
): ErrorDescription | undefined => {
    return (
        translated.clientError[exception?.message as ErrorCodesBackend] ??
        translated.clientError[exception?.errorName as ErrorCodesBackend]
    );
};

const ErrorCardWrapper: React.FC<ErrorProps> = ({
    children,
    className,
    title,
    exception,
    render,
    iconType,
    isModal,
    defaultError,
    hideErrorCode,
}) => {
    const translated = useTranslation();

    const knowExcepton = exception as RequestExceptionWithResponse;

    const isTimeout = knowExcepton?.code === ErrorCodes.TIMEOUT;
    const knownError = findKnownError(knowExcepton, translated);
    const errorTitle = knownError?.title || title || translated.errors.somethingWentWrong;

    const errorDescription =
        knownError?.description ||
        render?.(knowExcepton?.code as ErrorCodes) ||
        defaultRender(translated.errors, defaultError);

    return (
        <ErrorCard
            isModal={isModal}
            icon={iconType || selectIcon(isTimeout)}
            title={errorTitle}
            className={className}
            exception={knowExcepton}
            description={errorDescription}
            hideErrorCode={hideErrorCode}
        >
            {children}
        </ErrorCard>
    );
};

export default ErrorCardWrapper;
